import * as React from 'react'
import styled from 'styled-components'
import I18n from 'core/i18n'
import * as constants from 'static/constants'
import { ITourGuide } from 'static/interfaces'
import { StatusBadge } from 'components/atoms'
import { getStatusBadgeTypeFromStatus } from 'utils'

interface IProps {
  tourGuides: ITourGuide[]
}

export const GuideAssignmentStatusesLog: React.FC<IProps> = ({ tourGuides }) => {
  const groupedByDay = React.useMemo(() => {
    const groups: Record<string, ITourGuide[]> = {}
    tourGuides.forEach(tg => {
      const day = tg.tour.day
      if (!groups[day]) {
        groups[day] = []
      }
      groups[day].push(tg)
    })
    // 日付でソート
    return Object.keys(groups)
      .sort()
      .reduce((acc, key) => {
        acc[key] = groups[key]
        return acc
      }, {} as Record<string, ITourGuide[]>)
  }, [tourGuides])

  return (
    <Container>
      {Object.entries(groupedByDay).map(([day, guides]) => (
        <DaySection key={day}>
          <DayHeader>
            <div className="dayTitle">{formatDate(day)}</div>
            <div className="dayCount">
              <span className="count">{guides.length}</span> 件
            </div>
          </DayHeader>

          <TourList>
            {guides.map(tourGuide => (
              <TourCard key={tourGuide.id}>
                <TourInfo>
                  <a
                    className="tourName"
                    href={`/agent/tours/weekly?start=${day}&tour_id=${tourGuide.tour.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {tourGuide.tour.post.name}
                  </a>
                  <TourDetails>
                    <div className="detail">
                      <div className="label">{I18n.t('generic.start_time')}</div>
                      <div className="value">{tourGuide.tour.start_time}</div>
                    </div>
                    <div className="detail">
                      <div className="label">{I18n.t('generic.tour_type')}</div>
                      <div className="value">{tourGuide.tour.post.post_type}</div>
                    </div>
                  </TourDetails>
                </TourInfo>

                <StatusBadge
                  label={tourGuide.request_status_i18n}
                  type={getStatusBadgeTypeFromStatus(tourGuide.request_status)}
                />
              </TourCard>
            ))}
          </TourList>
        </DaySection>
      ))}
    </Container>
  )
}

const Container = styled.div`
  background-color: white;
  border-radius: 4px;
  overflow: hidden;
`

const DaySection = styled.div`
  &:last-child {
    border-bottom: none;
  }
`

const DayHeader = styled.div`
  background-color: ${constants.COLORS.background};
  padding: 8px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;

  .dayTitle {
    font-weight: bold;
  }

  .dayCount {
    padding: 4px 8px;
    .count {
      color: ${constants.COLORS.infoText};
      font-weight: bold;
    }
  }
`

const TourList = styled.div`
  display: flex;
  flex-direction: column;
`

const TourCard = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${constants.COLORS.divider2};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  &:last-child {
    border-bottom: none;
  }
`

const TourInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .tourName {
    font-size: 14px;
    font-weight: bold;
    color: ${constants.COLORS.primary};
  }
`

const TourDetails = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 14px;
  .detail {
    display: flex;
    gap: 4px;

    .label {
      color: ${constants.COLORS.subText};
    }
    .value {
      font-weight: 600;
      color: ${constants.COLORS.infoText};
    }
  }
`

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr + 'T00:00:00')
    return I18n.l(date, { format: 'date' })
  } catch {
    return dateStr
  }
}
